import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Clipboard, 
  Calendar,
  MapPin,
  Clock,
  Users,
  Trophy,
  CheckCircle,
  AlertCircle,
  Loader2,
  Flag,
  FileText
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const OfficialPortal = () => {
  const { user } = useAuth();
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');
  
  // Score reporting state
  const [showScoreDialog, setShowScoreDialog] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [team1Score, setTeam1Score] = useState('');
  const [team2Score, setTeam2Score] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      fetchSchedule();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchSchedule = async () => {
    try {
      const response = await axios.get(`${API}/officials/schedule`);
      setSchedule(response.data);
    } catch (error) {
      console.error('Failed to fetch schedule:', error);
      if (error.response?.status !== 401) {
        toast.error('Failed to load schedule');
      }
    } finally {
      setLoading(false);
    }
  };

  const openScoreDialog = (game) => {
    setSelectedGame(game);
    setTeam1Score(game.team1_score?.toString() || '0');
    setTeam2Score(game.team2_score?.toString() || '0');
    setNotes('');
    setShowScoreDialog(true);
  };

  const submitScore = async () => {
    if (!selectedGame) return;
    
    const score1 = parseInt(team1Score) || 0;
    const score2 = parseInt(team2Score) || 0;
    
    if (score1 < 0 || score2 < 0) {
      toast.error('Scores cannot be negative');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/officials/report-score`, {
        game_id: selectedGame.game_id,
        team1_score: score1,
        team2_score: score2,
        notes: notes || null
      });
      
      toast.success('Score reported successfully!');
      setShowScoreDialog(false);
      setSelectedGame(null);
      fetchSchedule();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to report score');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'scheduled':
        return <Badge variant="outline" className="bg-blue-500/10 text-blue-500 border-blue-500/20">Scheduled</Badge>;
      case 'live':
        return <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/20">🔴 Live</Badge>;
      case 'completed':
        return <Badge variant="outline" className="bg-gray-500/10 text-gray-500 border-gray-500/20">Completed</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const upcomingGames = schedule.filter(g => g.status === 'scheduled');
  const completedGames = schedule.filter(g => g.status === 'completed');
  const liveGames = schedule.filter(g => g.status === 'live');

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="text-center py-12">
          <CardContent>
            <Clipboard className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-xl font-bold mb-2">Login Required</h3>
            <p className="text-muted-foreground mb-6">
              Please login to access the Official Portal
            </p>
            <Link to="/login">
              <Button className="btn-primary">Login</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const GameCard = ({ game, showReportButton = false }) => (
    <Card className="card-hover" data-testid={`game-card-${game.game_id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            {getStatusBadge(game.status)}
            <Badge variant="secondary" className="text-xs">
              {game.official_role}
            </Badge>
          </div>
          <Badge variant="outline">{game.round}</Badge>
        </div>

        {/* Tournament Info */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
          <Trophy className="w-4 h-4" />
          <span className="font-medium">{game.tournament_name}</span>
        </div>

        {/* Teams */}
        <div className="space-y-2 mb-4">
          <div className={`flex items-center justify-between p-3 rounded-lg ${
            game.status === 'completed' && game.team1_score > game.team2_score
              ? 'bg-green-500/10 border border-green-500/30'
              : 'bg-muted/50'
          }`}>
            <span className="font-semibold">{game.team1_name}</span>
            {game.status === 'completed' && (
              <span className="font-mono text-xl font-bold">{game.team1_score}</span>
            )}
          </div>
          
          <div className="text-center text-xs text-muted-foreground">VS</div>
          
          <div className={`flex items-center justify-between p-3 rounded-lg ${
            game.status === 'completed' && game.team2_score > game.team1_score
              ? 'bg-green-500/10 border border-green-500/30'
              : 'bg-muted/50'
          }`}>
            <span className="font-semibold">{game.team2_name}</span>
            {game.status === 'completed' && (
              <span className="font-mono text-xl font-bold">{game.team2_score}</span>
            )}
          </div>
        </div>

        {/* Game Details */}
        <div className="grid grid-cols-2 gap-3 text-sm mb-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Clock className="w-4 h-4" />
            <span>{formatTime(game.scheduled_time)}</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <MapPin className="w-4 h-4" />
            <span>{game.field}</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground col-span-2">
            <Calendar className="w-4 h-4" />
            <span>{formatDateTime(game.scheduled_time)}</span>
          </div>
        </div>

        {/* Action Button */}
        {showReportButton && game.status !== 'completed' && (
          <Button
            onClick={() => openScoreDialog(game)}
            className="w-full btn-primary gap-2"
            data-testid={`report-score-btn-${game.game_id}`}
          >
            <FileText className="w-4 h-4" />
            Report Score
          </Button>
        )}
        
        {game.status === 'completed' && (
          <div className="flex items-center justify-center gap-2 text-green-500 text-sm">
            <CheckCircle className="w-4 h-4" />
            Score Reported
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="official-portal-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center">
            <Clipboard className="w-6 h-6 text-primary-foreground" />
          </div>
          <div>
            <h1 className="text-3xl sm:text-4xl font-black">
              <span className="text-primary">OFFICIAL PORTAL</span>
            </h1>
            <p className="text-muted-foreground">
              View your schedule and report game scores
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <Card data-testid="stat-upcoming">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-black">{upcomingGames.length}</p>
                <p className="text-sm text-muted-foreground">Upcoming Games</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-live">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <Flag className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-black">{liveGames.length}</p>
                <p className="text-sm text-muted-foreground">Live Now</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-completed">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gray-500/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-gray-500" />
              </div>
              <div>
                <p className="text-2xl font-black">{completedGames.length}</p>
                <p className="text-sm text-muted-foreground">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Schedule Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="upcoming" className="gap-2">
            <Calendar className="w-4 h-4" />
            Upcoming ({upcomingGames.length})
          </TabsTrigger>
          <TabsTrigger value="completed" className="gap-2">
            <CheckCircle className="w-4 h-4" />
            Completed ({completedGames.length})
          </TabsTrigger>
          <TabsTrigger value="all" className="gap-2">
            All ({schedule.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upcoming">
          {upcomingGames.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {upcomingGames.map(game => (
                <GameCard key={game.game_id} game={game} showReportButton={true} />
              ))}
            </div>
          ) : (
            <Card className="text-center py-12">
              <CardContent>
                <Calendar className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
                <h3 className="text-xl font-bold mb-2">No Upcoming Games</h3>
                <p className="text-muted-foreground">
                  You don't have any games scheduled yet
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="completed">
          {completedGames.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {completedGames.map(game => (
                <GameCard key={game.game_id} game={game} showReportButton={false} />
              ))}
            </div>
          ) : (
            <Card className="text-center py-12">
              <CardContent>
                <CheckCircle className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
                <h3 className="text-xl font-bold mb-2">No Completed Games</h3>
                <p className="text-muted-foreground">
                  Games you've officiated will appear here
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="all">
          {schedule.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {schedule.map(game => (
                <GameCard 
                  key={game.game_id} 
                  game={game} 
                  showReportButton={game.status !== 'completed'} 
                />
              ))}
            </div>
          ) : (
            <Card className="text-center py-12">
              <CardContent>
                <Clipboard className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
                <h3 className="text-xl font-bold mb-2">No Assignments</h3>
                <p className="text-muted-foreground">
                  You haven't been assigned to any games yet
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Score Report Dialog */}
      <Dialog open={showScoreDialog} onOpenChange={setShowScoreDialog}>
        <DialogContent className="sm:max-w-md" data-testid="score-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-black">REPORT SCORE</DialogTitle>
            <DialogDescription>
              Enter the final score for this game
            </DialogDescription>
          </DialogHeader>

          {selectedGame && (
            <div className="space-y-6">
              {/* Game Info */}
              <div className="p-3 rounded-lg bg-muted/50 text-sm">
                <p className="font-medium">{selectedGame.tournament_name}</p>
                <p className="text-muted-foreground">{selectedGame.round} • {selectedGame.field}</p>
              </div>

              {/* Team 1 Score */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  {selectedGame.team1_name}
                </Label>
                <Input
                  type="number"
                  min="0"
                  value={team1Score}
                  onChange={(e) => setTeam1Score(e.target.value)}
                  placeholder="0"
                  className="text-center text-2xl font-mono h-14"
                  data-testid="team1-score-input"
                />
              </div>

              {/* Team 2 Score */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  {selectedGame.team2_name}
                </Label>
                <Input
                  type="number"
                  min="0"
                  value={team2Score}
                  onChange={(e) => setTeam2Score(e.target.value)}
                  placeholder="0"
                  className="text-center text-2xl font-mono h-14"
                  data-testid="team2-score-input"
                />
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any additional notes about the game..."
                  rows={2}
                  data-testid="score-notes-input"
                />
              </div>

              {/* Score Preview */}
              <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
                <p className="text-sm text-muted-foreground mb-2">Final Score</p>
                <div className="flex items-center justify-center gap-4 text-2xl font-mono font-bold">
                  <span>{selectedGame.team1_name}</span>
                  <span className="text-primary">{team1Score || 0}</span>
                  <span className="text-muted-foreground">-</span>
                  <span className="text-primary">{team2Score || 0}</span>
                  <span>{selectedGame.team2_name}</span>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScoreDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={submitScore}
              disabled={submitting}
              className="btn-primary gap-2"
              data-testid="submit-score-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Submit Score
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
